import React, { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useAuth } from "../../common/contexts/AuthContext";
import {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  publishAnnouncement,
} from "../../api/announcementApi";

const AUDIENCE_OPTIONS = [
  { label: "All Staff", value: "all" },
  { label: "Teachers Only", value: "teachers" },
  { label: "Parents Only", value: "parents" },
];

const AUDIENCE_LABELS = {
  all: { label: "All Staff", severity: "info" },
  teachers: { label: "Teachers", severity: "success" },
  parents: { label: "Parents", severity: "warning" },
};

const emptyForm = { title: "", body: "", target_audience: "all" };

export default function AnnouncementsPage() {
  const toast = useRef(null);
  const { user, impersonatedUser } = useAuth() || {};
  const effectiveUser = impersonatedUser || user;
  const roleName =
    effectiveUser?.roleName ||
    effectiveUser?.role?.name ||
    effectiveUser?.Role?.name;
  const isAdmin = ["Admin", "Management", "SuperAdmin"].includes(roleName);

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editingPublished, setEditingPublished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(null); // id being published

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAnnouncements();
      setAnnouncements(data.items || []);
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load announcements" });
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setEditingPublished(false);
    setDialogVisible(true);
  };

  const openEdit = (item) => {
    setForm({ title: item.title, body: item.body, target_audience: item.target_audience });
    setEditingId(item.id);
    setEditingPublished(!!item.is_published);
    setDialogVisible(true);
  };

  const handleSave = async (publishNow = false) => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Title and body are required" });
      return;
    }
    setSaving(publishNow ? "publish" : "draft");
    try {
      let id = editingId;
      if (editingId) {
        await updateAnnouncement(editingId, form);
      } else {
        const res = await createAnnouncement(form);
        id = res.item?.id;
      }
      if (publishNow && id) {
        const res = await publishAnnouncement(id);
        toast.current?.show({
          severity: "success",
          summary: "Published",
          detail: `Announcement published. ${res.notified} user(s) notified.`,
        });
      } else {
        toast.current?.show({
          severity: "success",
          summary: editingId ? "Updated" : "Saved",
          detail: editingId ? "Announcement updated" : "Saved as draft — click Publish when ready.",
        });
      }
      setDialogVisible(false);
      load();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: err?.response?.data?.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (item) => {
    setPublishing(item.id);
    try {
      const res = await publishAnnouncement(item.id);
      toast.current?.show({
        severity: "success",
        summary: "Published",
        detail: `Announcement published. ${res.notified} user(s) notified.`,
      });
      load();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: err?.response?.data?.message || "Publish failed" });
    } finally {
      setPublishing(null);
    }
  };

  const handleDelete = (item) => {
    confirmDialog({
      message: `Delete "${item.title}"?`,
      header: "Confirm Delete",
      icon: "pi pi-trash",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteAnnouncement(item.id);
          toast.current?.show({ severity: "success", summary: "Deleted", detail: "Announcement deleted" });
          load();
        } catch {
          toast.current?.show({ severity: "error", summary: "Error", detail: "Delete failed" });
        }
      },
    });
  };

  const audienceTag = (audience) => {
    const info = AUDIENCE_LABELS[audience] || { label: audience, severity: "secondary" };
    return <Tag value={info.label} severity={info.severity} />;
  };

  const statusTag = (item) =>
    item.is_published ? (
      <Tag value="Published" severity="success" icon="pi pi-check-circle" />
    ) : (
      <Tag value="Draft" severity="secondary" icon="pi pi-clock" />
    );

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "—";

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold m-0">Announcements</h2>
          <p className="text-color-secondary text-sm mt-1">
            {isAdmin ? "Create and publish announcements to teachers and/or parents." : "School announcements for you."}
          </p>
        </div>
        {isAdmin && (
          <Button label="New Announcement" icon="pi pi-plus" onClick={openNew} />
        )}
      </div>

      {loading ? (
        <div className="flex justify-content-center p-6">
          <i className="pi pi-spin pi-spinner text-3xl text-primary" />
        </div>
      ) : announcements.length === 0 ? (
        <Card>
          <div className="text-center p-6">
            <i className="pi pi-megaphone text-5xl text-color-secondary opacity-40" />
            <p className="mt-3 text-color-secondary">No announcements yet.</p>
            {isAdmin && (
              <Button label="Create First Announcement" icon="pi pi-plus" className="mt-2" onClick={openNew} />
            )}
          </div>
        </Card>
      ) : (
        <div className="flex flex-column gap-3">
          {announcements.map((item) => (
            <Card key={item.id} className={`announcement-card ${!item.is_published ? "opacity-70" : ""}`}>
              <div className="flex align-items-start justify-content-between gap-3">
                {/* Left: icon + content */}
                <div className="flex gap-3 flex-1">
                  <div
                    className="flex-shrink-0 flex align-items-center justify-content-center border-circle"
                    style={{
                      width: "2.5rem",
                      height: "2.5rem",
                      background: item.is_published ? "var(--primary-100)" : "var(--surface-200)",
                    }}
                  >
                    <i
                      className="pi pi-megaphone"
                      style={{ color: item.is_published ? "var(--primary-color)" : "var(--text-color-secondary)" }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex align-items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-lg">{item.title}</span>
                      {audienceTag(item.target_audience)}
                      {statusTag(item)}
                    </div>
                    <p className="text-color-secondary m-0 line-height-3" style={{ whiteSpace: "pre-wrap" }}>
                      {item.body}
                    </p>
                    <div className="mt-2 text-xs text-color-secondary flex gap-3 flex-wrap">
                      <span>
                        <i className="pi pi-calendar mr-1" />
                        Created {formatDate(item.createdAt)}
                      </span>
                      {item.is_published && item.published_at && (
                        <span>
                          <i className="pi pi-send mr-1" />
                          Published {formatDate(item.published_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: admin actions */}
                {isAdmin && (
                  <div className="flex gap-1 flex-shrink-0">
                    {!item.is_published && (
                      <Button
                        icon="pi pi-send"
                        label="Publish"
                        size="small"
                        severity="success"
                        loading={publishing === item.id}
                        onClick={() => handlePublish(item)}
                        tooltip="Publish & notify recipients"
                        tooltipOptions={{ position: "top" }}
                      />
                    )}
                    <Button
                      icon="pi pi-pencil"
                      size="small"
                      text
                      onClick={() => openEdit(item)}
                      tooltip={item.is_published ? "Edit content (won't resend notifications)" : "Edit"}
                      tooltipOptions={{ position: "top" }}
                    />
                    <Button
                      icon="pi pi-trash"
                      size="small"
                      text
                      severity="danger"
                      onClick={() => handleDelete(item)}
                      tooltip="Delete"
                      tooltipOptions={{ position: "top" }}
                    />
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        header={editingId ? "Edit Announcement" : "New Announcement"}
        visible={dialogVisible}
        style={{ width: "min(560px, 95vw)" }}
        onHide={() => setDialogVisible(false)}
        footer={
          <div className="flex justify-content-between align-items-center">
            <Button label="Cancel" text onClick={() => setDialogVisible(false)} />
            <div className="flex gap-2">
              <Button
                label={editingId ? "Update" : "Save as Draft"}
                icon="pi pi-save"
                outlined
                loading={saving === "draft"}
                disabled={saving === "publish"}
                onClick={() => handleSave(false)}
              />
              {!editingPublished && (
                <Button
                  label="Publish Now"
                  icon="pi pi-send"
                  severity="success"
                  loading={saving === "publish"}
                  disabled={saving === "draft"}
                  onClick={() => handleSave(true)}
                />
              )}
            </div>
          </div>
        }
      >
        <div className="flex flex-column gap-4 pt-1">
          <div>
            <label className="font-medium block mb-2">Title *</label>
            <InputText
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Announcement title"
              className="w-full"
            />
          </div>

          <div>
            <label className="font-medium block mb-2">Message *</label>
            <InputTextarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Write your announcement here..."
              rows={5}
              autoResize
              className="w-full"
            />
          </div>

          {!editingPublished && (
            <div>
              <label className="font-medium block mb-2">Audience</label>
              <SelectButton
                value={form.target_audience}
                options={AUDIENCE_OPTIONS}
                onChange={(e) => setForm((f) => ({ ...f, target_audience: e.value || f.target_audience }))}
                className="w-full"
              />
              <p className="text-xs text-color-secondary mt-2">
                <i className="pi pi-info-circle mr-1" />
                {form.target_audience === "teachers" && "Only teachers will receive this announcement."}
                {form.target_audience === "parents" && "Only parents will receive this announcement."}
                {form.target_audience === "all" && "All staff, teachers, and parents will receive this announcement."}
              </p>
            </div>
          )}

          <div className={`p-3 border-round text-sm ${editingPublished ? "surface-200" : "surface-100"} text-color-secondary`}>
            <i className="pi pi-info-circle mr-2" />
            {editingPublished
              ? "This announcement is already published. Editing will update the content for readers without resending notifications."
              : <><strong>Save as Draft</strong> saves without notifying anyone. <strong>Publish Now</strong> sends it immediately and notifies all recipients via the notification bell.</>
            }
          </div>
        </div>
      </Dialog>
    </div>
  );
}
